import { Chip, IconName } from "@narmi/design_system";
import { catcat as cc } from "cerulean";
import { DisplayStatus as PaymentActivityStatus } from "byzantine/src/dbbl/businessLogic/entities/paymentActivity";
import { DisplayStatus as BulkStatus } from "byzantine/src/dbbl/businessLogic/entities/bulkPayments";
import { DisplayStatus as BulkTemplateStatus } from "byzantine/src/dbbl/businessLogic/entities/bulkTemplates";
import styles from "./StatusTag.module.scss";

type StatusType =
  | (typeof PaymentActivityStatus)[keyof typeof PaymentActivityStatus]
  | (typeof BulkStatus)[keyof typeof BulkStatus]
  | (typeof BulkTemplateStatus)[keyof typeof BulkTemplateStatus];

const AllStatus = {
  ...PaymentActivityStatus,
  ...BulkStatus,
  ...BulkTemplateStatus,
};

type StatusBadgeData = {
  className: string;
  dotClassName: string;
  kind: React.ComponentProps<typeof Chip>["kind"];
  iconName?: IconName;
};

const getStatusBadgeData = (status: StatusType): StatusBadgeData => {
  switch (status) {
    case AllStatus.pending_approval:
      return {
        className: styles.pendingBadge,
        dotClassName: styles.dotPendingApproval,
        iconName: "hourglass",
        kind: "warn",
      };
    case AllStatus.processing:
    case AllStatus.in_progress:
    case AllStatus.scheduled:
    case AllStatus.active_pending:
      return {
        className: styles.scheduledBadge,
        dotClassName: styles.dotActivePending,
        iconName: "clock",
        kind: "info",
      };
    case AllStatus.bulk_payment_canceled:
    case AllStatus.partially_completed:
    case AllStatus.canceled:
    case AllStatus.failed:
    case AllStatus.rejected:
      return {
        className: styles.failedBadge,
        dotClassName: styles.dotRejected,
        iconName: "x",
        kind: "error",
      };
    case AllStatus.archived:
      return {
        className: styles.archivedBadge,
        dotClassName: styles.dotArchived,
        iconName: "archive",
        kind: "primary",
      };
    case AllStatus.completed:
    case AllStatus.active:
      return {
        className: styles.completedBadge,
        dotClassName: styles.dotActive,
        iconName: "check",
        kind: "success",
      };
    default:
      return {
        className: styles.completedBadge,
        dotClassName: styles.dotActive,
        iconName: undefined,
        kind: "success",
      };
  }
};

export const getStatusDotClass = (status: StatusType): string =>
  getStatusBadgeData(status).dotClassName;

const StatusTag = ({
  status,
  isBadge,
  hideIcon,
}: {
  status: StatusType;
  isBadge?: boolean;
  hideIcon?: boolean;
}) => {
  const statusBadgeData = getStatusBadgeData(status);

  return isBadge ? (
    <div
      className={cc(statusBadgeData.className, styles.badge)}
      role="status"
      aria-label={status}
    >
      <span className={`narmi-icon-${statusBadgeData.iconName}`} />
    </div>
  ) : (
    <Chip
      kind={statusBadgeData.kind}
      label={status}
      startIcon={hideIcon ? undefined : statusBadgeData?.iconName}
    />
  );
};

export default StatusTag;
